import { ref, onUnmounted } from 'vue'
import { trackEvent, setClarityTag } from '~/composables/analytics'
import { FEEDBACK_PROMPT_DELAY_MS, FEEDBACK_THANKS_MS } from '~/utils/feedback'

export type FeedbackState = 'hidden' | 'ask' | 'why' | 'thanks'

/** What the visitor is being asked to rate. */
export interface FeedbackTarget {
  /**
   * The url exactly as it was sent upstream, taken from the response's own
   * `meta.url` rather than from the input box — editing the box after a
   * generation would otherwise rate a palette nobody saw.
   */
  url: string
  /**
   * `meta.requestId`: a per-call trace id from the Prefetch API, NOT the
   * requests row id (the server resolves the row itself). Used here purely as
   * the identity of "this result", so re-generating re-prompts and an already
   * rated palette doesn't.
   */
  traceId?: string | null
}

/**
 * Drives the "are you happy with this palette?" prompt under the results.
 *
 * A factory, not shared state — one instance per page, like the other palette
 * composables.
 *
 * Two writes on the unhappy path, on purpose: clicking "No" posts the negative
 * rating straight away, and picking a reason posts again. Most people close a
 * follow-up question without answering it, and a bare "this was bad" is still
 * the signal worth having. The endpoint upserts on the request row, so the
 * second write refines the first rather than duplicating it.
 */
export function useResultFeedback() {
  const state = ref<FeedbackState>('hidden')
  const reason = ref<string | null>(null)
  const note = ref('')
  const submitting = ref(false)
  const error = ref('')

  const target = ref<FeedbackTarget | null>(null)

  // Answered or dismissed prompts. Nothing here is ever asked about twice.
  const handled = new Set<string>()

  let showTimer: ReturnType<typeof setTimeout> | undefined
  let hideTimer: ReturnType<typeof setTimeout> | undefined

  /**
   * Identity of a result. Trace ids are unique per call, so re-generating the
   * same url asks again — which is right, it's a different palette. Without a
   * trace id (an older or cached envelope) the url alone has to serve, and the
   * cost is only that a re-generation of that one url won't re-prompt.
   */
  function keyOf(t: FeedbackTarget | null) {
    if (!t?.url) return ''
    return `${t.url}|${t.traceId ?? ''}`
  }

  /**
   * Host of the rated url — the analytics context every event below carries.
   *
   * The host, not the url: the full string can carry a path and query off
   * someone else's site, and "which sites do people dislike the palette for?"
   * is a question about the domain. Same shape the extension handoff already
   * reports as `site`, so the two line up in one report.
   */
  function siteOf(t: FeedbackTarget | null) {
    const raw = t?.url?.trim()
    if (!raw) return ''
    return raw.replace(/^https?:\/\//i, '').split(/[/?#]/)[0].toLowerCase()
  }

  /** GA params for a feedback event. Clarity ignores these — see the tags below. */
  function eventParams(extra?: Record<string, unknown>) {
    return { site: siteOf(target.value), ...extra }
  }

  function clearTimers() {
    if (showTimer) clearTimeout(showTimer)
    if (hideTimer) clearTimeout(hideTimer)
    showTimer = undefined
    hideTimer = undefined
  }

  function reset() {
    clearTimers()
    state.value = 'hidden'
    reason.value = null
    note.value = ''
    error.value = ''
    submitting.value = false
  }

  /**
   * Offer the prompt for a freshly rendered palette. Pass null (nothing
   * generated yet, a generation in flight, or an error) to take the bar down.
   */
  function prompt(next: FeedbackTarget | null) {
    const key = keyOf(next)

    if (!key || handled.has(key)) {
      // Don't wipe a prompt the visitor is part-way through answering.
      if (state.value !== 'why') reset()
      return
    }
    // Already showing, scheduled, or thanking for exactly this result. The page
    // hands us a fresh object on every render, so without this the show timer
    // would restart on each one and the bar would never appear.
    if (key === keyOf(target.value)) return

    reset()
    target.value = next

    // Let the palette land first — a bar that slides in over a still-rendering
    // result reads as an error toast.
    showTimer = setTimeout(() => {
      state.value = 'ask'
      trackEvent('Palette Feedback Shown', eventParams())
    }, FEEDBACK_PROMPT_DELAY_MS)
  }

  async function send(rating: 'positive' | 'negative', withDetail: boolean) {
    const current = target.value
    if (!current) return false
    submitting.value = true
    error.value = ''
    try {
      await $fetch('/api/feedback', {
        method: 'POST',
        body: {
          url: current.url,
          rating,
          ...(current.traceId ? { traceId: current.traceId } : {}),
          ...(withDetail && reason.value ? { reason: reason.value } : {}),
          ...(withDetail && note.value.trim() ? { note: note.value.trim() } : {}),
        },
      })
      return true
    } catch (e: unknown) {
      // Deliberately not the raw error: the only ones reachable here are a
      // network blip or a result too old to resolve, and "[POST] /api/feedback
      // 404" tells nobody anything they can act on. The real error still reaches
      // the console — including the handler's own message, which ofetch buries
      // in `data` and which is the only part that says *why* it was rejected.
      const detail = (e as { data?: { message?: string } })?.data?.message
      console.error('[feedback] submit failed:', detail ?? e, { target: current })
      error.value = 'Could not save that — please try again.'
      trackEvent('Palette Feedback Error', eventParams({ rating, step: state.value }))
      return false
    } finally {
      submitting.value = false
    }
  }

  function finish() {
    handled.add(keyOf(target.value))
    state.value = 'thanks'
    hideTimer = setTimeout(() => { state.value = 'hidden' }, FEEDBACK_THANKS_MS)
  }

  /** 👍 — record it and get out of the way. */
  async function rateYes() {
    trackEvent('Palette Feedback Positive', eventParams())
    // A Clarity event is a bare name, so it can't be filtered on afterwards; a
    // session tag can. This is what makes "watch the sessions of people who
    // hated their palette" a filter in the recordings list.
    setClarityTag('palette_feedback', 'positive')
    const ok = await send('positive', false)
    if (ok) finish()
  }

  /** 👎 — record the complaint now, ask what went wrong second. */
  async function rateNo() {
    trackEvent('Palette Feedback Negative', eventParams())
    setClarityTag('palette_feedback', 'negative')
    state.value = 'why'
    await send('negative', false)
    // A failure here surfaces in `error` but doesn't block the follow-up: the
    // reason submit re-sends everything anyway.
  }

  function selectReason(slug: string) {
    reason.value = reason.value === slug ? null : slug
  }

  /** Submit the follow-up. Also the "skip" path — reason and note may be empty. */
  async function submitDetail() {
    const slug = reason.value
    const hasNote = note.value.trim().length > 0

    // Two events for one submit, because the vendors break it down differently.
    // Clarity can only group by event name, so the reason has to be *in* the
    // name there. GA groups by param instead, and needs to: toGa4EventName caps
    // names at 40 characters, which clips the tail off the longest slug.
    if (slug) {
      trackEvent(`Palette Feedback Reason (${slug})`)
      setClarityTag('palette_feedback_reason', slug)
    }
    // Fires either way — the same button is "Skip" with nothing filled in, and
    // how often the follow-up gets closed empty is the number that says whether
    // asking a second question is worth it at all.
    trackEvent(
      slug || hasNote ? 'Palette Feedback Detail Sent' : 'Palette Feedback Detail Skipped',
      eventParams({ reason: slug ?? 'none', has_note: hasNote }),
    )

    const ok = await send('negative', true)
    if (ok) finish()
  }

  /** ✕ — they don't want to answer. Don't ask again for this palette. */
  function dismiss() {
    trackEvent('Palette Feedback Dismissed', eventParams({ step: state.value }))
    // Only an unanswered prompt earns a tag of its own: closing the follow-up
    // is already covered by the `negative` tag the thumbs-down set.
    if (state.value === 'ask') setClarityTag('palette_feedback', 'dismissed')
    handled.add(keyOf(target.value))
    reset()
  }

  onUnmounted(clearTimers)

  return { state, reason, note, submitting, error, prompt, rateYes, rateNo, selectReason, submitDetail, dismiss }
}
